import { useCallback, useMemo, useState } from 'react'

import { FilterCompanyBankAccountsWebPage } from 'commons/service/bank/companyDetails'
import { BankAccount } from 'commons/types/DTO/bank/companyDetails/bankAccount'
import { Toast } from 'ui/components'
import { useTableState } from 'ui/components/Table'
import { useConfirmation } from 'ui/hooks'

import { hooks as bankAccountHooks } from '@orbit/bank-accounts'
import { handlerServerError } from '@orbit/errors'

export const useComponentsEssence = (
  companyId: number,
  filterCompanyBankAccountsWebPage: (filter: FilterCompanyBankAccountsWebPage) => void,
  bankAccountsTotalCount: number
) => {
  const [loading, setLoading] = useState(false)

  const defaultFilter = useMemo(
    () => ({
      relationshipId: companyId,
      page: 1,
      size: 10
    }),
    []
  )

  const [tableFilter, setTableFilter] = useState<{
    relationshipId: number
    page: number
    size: number
  }>(defaultFilter)

  const tableState = useTableState({ filter: {} })

  const changePagination = useCallback(
    async (page: number, size: number) => {
      setLoading(true)

      tableState.onPageChange(page, size)

      const filter = { ...tableFilter, page, size }

      setTableFilter(filter)

      await filterCompanyBankAccountsWebPage(filter)

      setLoading(false)
    },
    [filterCompanyBankAccountsWebPage, tableFilter, tableState]
  )

  const getCompanyBankAccountsAfterDeleteWebPage = () => {
    setLoading(true)

    const isDeletingBankAccountsLastOnPage = tableFilter.size * tableFilter.page >= bankAccountsTotalCount

    const page = isDeletingBankAccountsLastOnPage
      ? Math.ceil((bankAccountsTotalCount - 1) / tableFilter.size) || 1
      : tableFilter.page

    filterCompanyBankAccountsWebPage({
      ...tableFilter,
      page
    })

    tableState.onPageChange(page, tableState.size)

    const filter = { ...tableFilter, page }

    setTableFilter(filter)

    setLoading(false)
  }

  const [accountToDelete, setAccountToDelete] = useState<BankAccount | undefined>(undefined)

  const { mutate: deleteAccount } = bankAccountHooks.useDelete({
    onError: (error) => handlerServerError(error),
    onSuccess: async () => {
      getCompanyBankAccountsAfterDeleteWebPage()
      Toast.successSmall(`Bank Account "${accountToDelete?.accountNumber}" was successfully deleted.`)
    }
  })

  const { open, Confirmation } = useConfirmation({
    message: `Are you sure you want to remove this bank account?`,
    onConfirm: () => (accountToDelete?.id ? deleteAccount({ id: accountToDelete?.id }) : null),
    confirmationButtonText: 'Delete',
    isConfirmationNegative: true
  })

  const startDeleting = useCallback(
    (item: any) => {
      setAccountToDelete(item)
      open()
    },
    [open, setAccountToDelete]
  )

  return {
    loading,
    changePagination,
    tableState,
    Confirmation,
    startDeleting
  }
}
